import React, { useState, useEffect } from 'react';
import { Card, Tabs, Form, Input, Button, message, Space, Divider, Typography } from 'antd';
import { RollbackOutlined, SaveOutlined, ReloadOutlined } from '@ant-design/icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  getAllWxPusherTemplates,
  getWxPusherTemplateByKey,
  updateWxPusherTemplate,
  resetWxPusherTemplate,
  testWxPusherTemplate,
  getAllWxPusherConfigs
} from '../services/wxPusherService';
import { useNavigate } from 'react-router-dom';

const { Title, Paragraph, Text } = Typography;
const { TabPane } = Tabs;

// 富文本编辑器配置
const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'color': [] }, { 'background': [] }],
    ['link'],
    ['clean']
  ],
};

const WxPusherTemplates = () => {
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [activeKey, setActiveKey] = useState('');
  const [form] = Form.useForm();
  const [configs, setConfigs] = useState([]);
  const [testLoading, setTestLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchTemplates();
    fetchConfigs();
  }, []);
  
  // 获取所有微信推送配置
  const fetchConfigs = async () => {
    try {
      // 使用已导入的getAllWxPusherConfigs方法
      const data = await getAllWxPusherConfigs();
      setConfigs(data || []);
    } catch (error) {
      console.error('获取微信推送配置失败:', error);
      message.error('获取微信推送配置失败');
    }
  };

  // 获取所有模板
  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const data = await getAllWxPusherTemplates();
      setTemplates(data);
      
      // 设置初始活动标签
      if (data && data.length > 0) {
        setActiveKey(data[0].template_key);
        await fetchTemplateDetail(data[0].template_key);
      }
    } catch (error) {
      message.error('获取模板列表失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取单个模板详情
  const fetchTemplateDetail = async (templateKey) => {
    try {
      setLoading(true);
      const template = await getWxPusherTemplateByKey(templateKey);
      form.setFieldsValue({
        templateName: template.template_name,
        templateContent: template.template_content
      });
    } catch (error) {
      message.error(`获取模板详情失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 切换标签页
  const handleTabChange = async (key) => {
    setActiveKey(key);
    await fetchTemplateDetail(key);
  };

  // 提交表单保存模板
  const handleSubmit = async (values) => {
    try {
      setLoading(true);
      const { templateName, templateContent } = values;
      
      const result = await updateWxPusherTemplate(activeKey, {
        templateName,
        templateContent
      });
      
      if (result.success) {
        message.success('模板更新成功');
        fetchTemplates();
      } else {
        message.error(`更新失败: ${result.error}`);
      }
    } catch (error) {
      message.error(`保存模板失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // 重置模板为默认内容
  const handleReset = async () => {
    try {
      if (!window.confirm('确定要重置此模板为默认内容吗？此操作不可撤销。')) {
        return;
      }

      setLoading(true);
      const result = await resetWxPusherTemplate(activeKey);
      
      if (result.success) {
        message.success('模板已重置为默认内容');
        await fetchTemplateDetail(activeKey);
      } else {
        message.error(`重置失败: ${result.error}`);
      }
    } catch (error) {
      message.error(`重置模板失败: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // 测试推送模板
  const handleTest = async () => {
    try {
      // 如果没有配置，提示错误
      if (configs.length === 0) {
        message.error('没有可用的微信推送配置，请先添加配置');
        return;
      }
      
      // 获取当前模板内容
      const values = await form.validateFields();
      const { templateContent } = values;
      
      // 找到有测试SPT的配置
      const testConfig = configs.find(c => c.test_spt);
      
      if (!testConfig) {
        message.error('没有找到测试SPT配置，请先在微信推送配置中添加测试SPT');
        return;
      }
      
      console.log('测试配置:', testConfig);
      
      // 生成测试数据
      const testData = {};
      
      // 根据不同模板类型生成测试数据
      if (activeKey === 'shipment_create' || activeKey === 'shipment_update') {
        testData.id = '12345';
        testData.create_time = new Date().toLocaleString();
        testData.update_time = new Date().toLocaleString();
        testData.status_text = '已发货';
        testData.status_color = '#52c41a';
        testData.name = '测试客户';
        testData.phone = '13800138000';
        testData.date = new Date().toLocaleDateString();
        testData.cost = '100.00';
        testData.country = '美国';
        testData.tracking_no = 'TEST123456789';
      } else if (activeKey === 'shipment_delete') {
        testData.id = '12345';
        testData.delete_time = new Date().toLocaleString();
        testData.name = '测试客户';
        testData.phone = '13800138000';
        testData.cost = '100.00';
      }
      
      setTestLoading(true);
      const result = await testWxPusherTemplate(activeKey, testConfig.id, testData);
      
      if (result.success) {
        message.success('测试模板推送成功，请查看微信');
      } else {
        message.error(`测试失败: ${result.error || '未知错误'}`);
      }
    } catch (error) {
      console.error('测试模板失败:', error);
      message.error(`测试模板失败: ${error.message || '未知错误'}`);
    } finally {
      setTestLoading(false);
    }
  };

  // 返回配置页面
  const handleBack = () => {
    navigate('/wx-pusher');
  };

  // 变量替换提示
  const renderVariablesHelp = () => {
    let variables = [];

    // 根据不同模板类型显示不同的变量提示
    if (activeKey === 'shipment_create' || activeKey === 'shipment_update') {
      variables = [
        '{{id}} - 运单ID',
        '{{create_time}} - 创建时间',
        '{{update_time}} - 更新时间',
        '{{status_text}} - 状态文本',
        '{{status_color}} - 状态颜色',
        '{{name}} - 客户名称',
        '{{phone}} - 客户电话',
        '{{date}} - 运单日期',
        '{{cost}} - 费用',
        '{{country}} - 目的地国家',
        '{{tracking_no}} - 物流单号'
      ];
    } else if (activeKey === 'shipment_delete') {
      variables = [
        '{{id}} - 运单ID',
        '{{delete_time}} - 删除时间',
        '{{name}} - 客户名称',
        '{{phone}} - 客户电话',
        '{{cost}} - 费用'
      ];
    }

    return (
      <div style={{ marginTop: 20 }}>
        <Divider orientation="left">可用变量</Divider>
        <Paragraph>
          您可以在模板中使用以下变量，系统将自动替换为对应的值：
        </Paragraph>
        <ul>
          {variables.map((v, index) => (
            <li key={index}>
              <Text code>{v}</Text>
            </li>
          ))}
        </ul>
      </div>
    );
  };

  return (
    <Card
      title="微信推送消息模板配置"
      loading={loading}
      extra={
        <Button
          type="primary"
          icon={<RollbackOutlined />}
          onClick={handleBack}
        >
          返回
        </Button>
      }
    >
      <Typography.Paragraph>
        在这里您可以自定义不同事件的微信推送消息模板。支持HTML格式，可以通过变量插入动态内容。
      </Typography.Paragraph>

      {templates.length > 0 ? (
        <Tabs activeKey={activeKey} onChange={handleTabChange}>
          {templates.map(template => (
            <TabPane tab={template.template_name} key={template.template_key}>
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
                initialValues={{
                  templateName: '',
                  templateContent: ''
                }}
              >
                <Form.Item
                  name="templateName"
                  label="模板名称"
                  rules={[{ required: true, message: '请输入模板名称' }]}
                >
                  <Input placeholder="请输入模板名称" />
                </Form.Item>

                <Form.Item
                  name="templateContent"
                  label="模板内容"
                  rules={[{ required: true, message: '请输入模板内容' }]}
                >
                  <ReactQuill
                    theme="snow"
                    modules={modules}
                    style={{ height: '300px', marginBottom: '50px' }}
                  />
                </Form.Item>

                {renderVariablesHelp()}

                <Form.Item>
                  <Space>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      htmlType="submit"
                      loading={loading}
                    >
                      保存
                    </Button>
                    <Button
                      danger
                      icon={<ReloadOutlined />}
                      onClick={handleReset}
                      loading={loading}
                    >
                      重置为默认
                    </Button>
                    <Button
                      type="primary"
                      style={{ backgroundColor: '#1890ff' }}
                      onClick={handleTest}
                      loading={testLoading}
                    >
                      测试推送
                    </Button>
                  </Space>
                </Form.Item>
              </Form>
            </TabPane>
          ))}
        </Tabs>
      ) : (
        <div style={{ textAlign: 'center', padding: '30px 0' }}>
          <p>未找到模板配置，请先添加模板</p>
        </div>
      )}
    </Card>
  );
};

export default WxPusherTemplates;
